

 /* New version */
/* =========================================================
   ProductInfo (dual-mode)
   - Legacy mode (variant): uses variant.featured_media -> updateMedia() -> mediaGallery.setActiveMedia()
   - Metaobject mode: listens for `color-pattern:change` and swaps the MAIN visible image using ColorPatternMap JSON
   - Both logics coexist safely: mode decided by data-color-picker-source="variant|metaobject"
========================================================= */
if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      quantityInputSticky = undefined;
      quantityFormSticky = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input-primary');
        this.quantityInputSticky = this.querySelector('.quantity__input-sticky');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput && !this.quantityInputSticky) return;

        this.quantityForm = this.querySelector('.product-form__quantity-primary');
        this.quantityFormSticky = this.querySelector('.product-form__quantity-sticky');
        if (!this.quantityForm && !this.quantityFormSticky) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push(() => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;

        const shouldSwapProduct =
          this.dataset.url !== productUrl || this.querySelector('li[data-gang-option]') !== null;

        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode
          ?.querySelector('variant-selects [data-selected-variant]')
          ?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];
        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);
        if (optionValues.length) params.push(`option_values=${optionValues.join(',')}`);
        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects[data-primary]');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
        const variantSelectsAlt = html.querySelector('variant-selects[data-sticky]');
        if (variantSelectsAlt) {
          HTMLUpdateUtility.viewTransition(this.variantSelectorsAlt, variantSelectsAlt, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          // Normal behavior: move gallery to featured media
          this.updateMedia(html, variant?.featured_media?.id);

          // ✅ NEW: force main image from variant metafield swatch_img (if exists)
          this.applyVariantSwatchMainImage(variant);

          // TA-style metaobject image swap (your existing logic)
          this.applyColorPatternMainImage(variant);

          const updateSourceFromDestination = (id, shouldHide = () => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Price-Alt');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateCustomLiquid(html);

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            theme.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: { sectionId: this.sectionId, html, variant },
          });
        };
      }

      updateCustomLiquid(html) {
        this.querySelectorAll('.custom__liquid').forEach((block) => {
          if (block.id) {
            const liquid = html.querySelector(`#${block.id}`);
            if (liquid) block.innerHTML = liquid.innerHTML;
          }
        });
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, #product-form-${this.dataset.section}--alt`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(`${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`);
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, theme.variantStrings.unavailable);

        const selectors = [
          'price',
          'Price-Alt',
          'Inventory',
          'Sku',
          'Price-Per-Item',
          'Volume-Note',
          'Volume',
          'Quantity-Rules',
        ]
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');

        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul') || this.querySelector('product-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`) || html.querySelector(`product-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }]));
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(mediaGalleryDestination.querySelectorAll('li[data-media-id]'));
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);
            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        const mediaGallery = this.querySelector('media-gallery');
        if (mediaGallery != null) {
          mediaGallery.setActiveMedia?.(`${this.dataset.section}-${variantFeaturedMediaId}`, true);
        } else {
          this.querySelector('[id^="Slider-"]')?.scrollTo({ left: 0 });
        }

        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      /**
       * ✅ NEW:
       * Force main image to variant metafield swatch_img.
       * - First tries variant.metafields.custom.swatch_img (if your JSON includes it)
       * - Fallback: reads VariantSwatchMap JSON from DOM (recommended)
       */
      applyVariantSwatchMainImage(variant) {
        const url = this.getVariantSwatchUrl(variant);
        if (!url) {
          this.restoreForcedMainImageIfAny('variant-swatch');
          return;
        }

        const img =
          this.querySelector('media-gallery .is-active img, media-gallery [aria-current="true"] img, media-gallery [data-media-id].is-active img') ||
          this.querySelector('.product__media img, .product-media img, .media-gallery__viewer img');

        if (!img) return;

        if (!img.dataset.originalSrc) img.dataset.originalSrc = img.getAttribute('src') || '';
        if (!img.dataset.originalSrcset) img.dataset.originalSrcset = img.getAttribute('srcset') || '';
        if (!img.dataset.originalSizes) img.dataset.originalSizes = img.getAttribute('sizes') || '';

        img.setAttribute('src', url);
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.removeAttribute('data-src');
        img.removeAttribute('data-srcset');

        img.dataset.forcedSwatch = 'variant-swatch';
      }

      getVariantSwatchUrl(variant) {
        // 1) If your selected variant JSON includes metafields (custom), use it
        try {
          const mf = variant?.metafields?.custom?.swatch_img;
          if (mf) {
            if (typeof mf === 'string') return mf;
            if (mf?.value) return mf.value;
            if (mf?.src) return mf.src;
          }
        } catch (e) {}

        // 2) Fallback to VariantSwatchMap (recommended)
        const mapScript =
          this.querySelector(`#VariantSwatchMap-${this.sectionId}[data-variant-swatch-map]`) ||
          this.querySelector(`#VariantSwatchMap-${this.dataset.section}[data-variant-swatch-map]`);

        if (!mapScript) return null;

        let map;
        try {
          map = JSON.parse(mapScript.textContent || '{}');
        } catch (e) {
          console.warn('VariantSwatchMap JSON parse failed', e);
          return null;
        }

        const id = variant?.id ? String(variant.id) : '';
        if (!id) return null;

        return map[id] || null;
      }

      /**
       * TA-style: When a swatch/color is selected, swap the MAIN product image to the Color metaobject image.
       * - Reads ColorPatternMap JSON (metaobjects['shopify--color-pattern'])
       * - Uses the current selected variant's "Color" option value (based on data-color-option-position)
       */
      applyColorPatternMainImage(variant) {
        const mapScript =
          this.querySelector(`#ColorPatternMap-${this.sectionId}[data-color-pattern-map]`) ||
          this.querySelector(`#ColorPatternMap-${this.dataset.section}[data-color-pattern-map]`);

        if (!mapScript) return;

        let map;
        try {
          map = JSON.parse(mapScript.textContent || '{}');
        } catch (e) {
          console.warn('ColorPatternMap JSON parse failed', e);
          return;
        }

        const pos = Number(this.dataset.colorOptionPosition || this.getAttribute('data-color-option-position') || 0);
        let raw = '';
        if (pos > 0) {
          const key = `option${pos}`;
          raw = variant && variant[key] ? String(variant[key]) : '';
          if (!raw && Array.isArray(variant?.options) && variant.options[pos - 1]) raw = String(variant.options[pos - 1]);
        }

        const normalize = (s) =>
          String(s || '')
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '')
            .replace(/[-_]/g, '');

        const valueNorm = normalize(raw);
        if (!valueNorm) {
          this.restoreForcedMainImageIfAny('color-pattern');
          return;
        }

        let url = map[valueNorm] || '';

        if (!url) {
          let bestKey = '';
          for (const k of Object.keys(map)) {
            if (!k) continue;
            if (valueNorm.includes(k) && k.length > bestKey.length && map[k]) {
              bestKey = k;
              url = map[k];
            }
          }
        }

        if (!url) {
          this.restoreForcedMainImageIfAny('color-pattern');
          return;
        }

        const img =
          this.querySelector('media-gallery .is-active img, media-gallery [aria-current="true"] img, media-gallery [data-media-id].is-active img') ||
          this.querySelector('.product__media img, .product-media img, .media-gallery__viewer img');

        if (!img) return;

        if (!img.dataset.originalSrc) img.dataset.originalSrc = img.getAttribute('src') || '';
        if (!img.dataset.originalSrcset) img.dataset.originalSrcset = img.getAttribute('srcset') || '';
        if (!img.dataset.originalSizes) img.dataset.originalSizes = img.getAttribute('sizes') || '';

        img.setAttribute('src', url);
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.removeAttribute('data-src');
        img.removeAttribute('data-srcset');

        img.dataset.forcedSwatch = 'color-pattern';
      }

      restoreForcedMainImageIfAny(kind) {
        const img =
          this.querySelector(`media-gallery .is-active img[data-forced-swatch="${kind}"], media-gallery [aria-current="true"] img[data-forced-swatch="${kind}"]`) ||
          this.querySelector(`.product__media img[data-forced-swatch="${kind}"], .product-media img[data-forced-swatch="${kind}"], .media-gallery__viewer img[data-forced-swatch="${kind}"]`);

        if (!img) return;

        const originalSrc = img.dataset.originalSrc || '';
        const originalSrcset = img.dataset.originalSrcset || '';
        const originalSizes = img.dataset.originalSizes || '';

        if (originalSrc) img.setAttribute('src', originalSrc);
        if (originalSrcset) img.setAttribute('srcset', originalSrcset);
        else img.removeAttribute('srcset');

        if (originalSizes) img.setAttribute('sizes', originalSizes);
        else img.removeAttribute('sizes');

        delete img.dataset.forcedSwatch;
      }

      setQuantityBoundries() {
        const input = this.quantityInput || this.quantityInputSticky;
        const data = {
          cartQuantity: input?.dataset.cartQuantity ? parseInt(input.dataset.cartQuantity) : 0,
          min: input?.dataset.min ? parseInt(input.dataset.min) : 1,
          max: input?.dataset.max ? parseInt(input.dataset.max) : null,
          step: input?.step ? parseInt(input.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        if (this.quantityInput) this.quantityInput.min = min;
        if (this.quantityInputSticky) this.quantityInputSticky.min = min;

        if (max) {
          if (this.quantityInput) this.quantityInput.max = max;
          if (this.quantityInputSticky) this.quantityInputSticky.max = max;
        } else {
          this.quantityInput?.removeAttribute('max');
          this.quantityInputSticky?.removeAttribute('max');
        }

        if (this.quantityInput) this.quantityInput.value = min;
        if (this.quantityInputSticky) this.quantityInputSticky.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput && !this.quantityInputSticky) return;
        this.setQuantityBoundries();

        const quantityFormUpdated =
          html.getElementById(`Quantity-Form-${sectionId}`) || html.getElementById(`Quantity-Form-${sectionId}-sticky`);

        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm?.querySelector(selector);
          const currentSticky = this.quantityFormSticky?.querySelector(selector);
          const updated = quantityFormUpdated?.querySelector(selector);

          if (!updated) continue;
          if (!current && !currentSticky) continue;

          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current?.setAttribute(attribute, valueUpdated);
                currentSticky?.setAttribute(attribute, valueUpdated);
              } else {
                current?.removeAttribute(attribute);
                currentSticky?.removeAttribute(attribute);
              }
            }
          } else {
            if (current) current.innerHTML = updated.innerHTML;
            if (currentSticky) currentSticky.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects[data-primary]');
      }

      get variantSelectorsAlt() {
        return this.querySelector('variant-selects[data-sticky]');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(SectionId.parseId(this.sectionId), 'related-products');
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(SectionId.parseId(this.sectionId), 'quick_order_list');
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

